import {
  Amplify
} from 'aws-amplify'
import {
  signIn,
  confirmSignIn,
  fetchAuthSession,
  signOut,
  rememberDevice,
  forgetDevice,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  getCurrentUser,
  resetPassword,
  confirmResetPassword,
  fetchMFAPreference
} from 'aws-amplify/auth'
import { cognitoPoolId, cognitoClientId } from '../../env'

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: cognitoPoolId,
      userPoolClientId: cognitoClientId,
    }
  }
})

// Device Expiration configuration
const DEVICE_TRUST_Duration_MS = 30 * 24 * 60 * 60 * 1000 // 30 Days
const DEVICE_TRUST_KEY = 'device_trusted_timestamp'

const cognitoProvider = {
  login: async ({ username, password, newPassword = undefined, mfaCode = undefined, session = undefined, challengeName = undefined, trustDevice = false }) => {
    try {

      let nextStep

      if (challengeName === 'SOFTWARE_TOKEN_MFA' || challengeName === 'SMS_MFA_CODE') {
        const output = await confirmSignIn({ challengeResponse: mfaCode })
        nextStep = output.nextStep
      } else {
        const output = await signIn({ username, password })
        nextStep = output.nextStep
      }

      // Handle Next Steps (Challenges)
      switch (nextStep.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
          return Promise.reject({
            mfaRequired: true,
            challengeName: 'SOFTWARE_TOKEN_MFA',
          })

        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          if (newPassword) {
            const output = await confirmSignIn({ challengeResponse: newPassword })
            if (output.nextStep.signInStep === 'DONE') {
              // success
            } else {
              return Promise.reject({
                mfaRequired: true,
                challengeName: 'SOFTWARE_TOKEN_MFA'
              })
            }
          } else {
            return Promise.reject({
              changeTempPassword: true,
              user: { username, password },
            })
          }
          break;

        case 'DONE':
          const trustedTimestamp = localStorage.getItem(DEVICE_TRUST_KEY)
          if (trustedTimestamp) {
            const expirationTime = parseInt(trustedTimestamp) + DEVICE_TRUST_Duration_MS
            if (Date.now() > expirationTime) {
              console.log('Device trust expired. Forgetting device and forcing MFA.')
              try {
                await forgetDevice()
                localStorage.removeItem(DEVICE_TRUST_KEY)
                await signOut()
                return cognitoProvider.login({ username, password })
              } catch (e) {
                console.error('Error handling device expiration', e)
              }
            }
          }
          break;

        default:
          console.error('Unhandled sign-in step:', nextStep.signInStep)
          return Promise.reject({ message: 'Unknown login step ' + nextStep.signInStep })
      }


      if (trustDevice) {
        try {
          await rememberDevice()
          localStorage.setItem(DEVICE_TRUST_KEY, Date.now().toString())
          console.log('Device remembered successfully')
        } catch (err) {
          console.error('Error remembering device:', err)
        }
      }

      const { tokens } = await fetchAuthSession()
      if (tokens) {
        localStorage.setItem('accessToken', tokens.accessToken.toString())
        localStorage.setItem('idToken', tokens.idToken.toString())
        localStorage.setItem('cognito_username', username || (await getCurrentUser()).username)
      } else {
        // Should not happen on DONE
      }

      return Promise.resolve()

    } catch (error) {
      console.error('Login Error:', error)
      return Promise.reject(error)
    }
  },

  logout: async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('idToken')
      localStorage.removeItem('cognito_username')
      return Promise.resolve()
    }
  },

  checkError: ({ status }) => {
    if (status === 401) {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('idToken')
      localStorage.removeItem('cognito_username')
      return Promise.reject({ redirectTo: '/login' })
    }
    return Promise.resolve()
  },

  checkAuth: async () => {
    try {
      const { tokens } = await fetchAuthSession()
      // Sync tokens
      if (tokens) {
        localStorage.setItem('accessToken', tokens.accessToken.toString())
        localStorage.setItem('idToken', tokens.idToken.toString())
        return Promise.resolve()
      }
      throw new Error('No tokens')
    } catch (error) {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('idToken')
      localStorage.removeItem('cognito_username')
      return Promise.reject({ redirectTo: '/login' })
    }
  },

  getPermissions: () => Promise.resolve(),

  getIdentity: async () => {
    try {
      const user = await getCurrentUser()
      return Promise.resolve({ fullName: user.username })
    } catch (e) {
      return Promise.resolve({ fullName: localStorage.getItem('cognito_username') })
    }
  },

  forgotPassword: ({ username }) => {
    return resetPassword({ username })
  },

  confirmPassword: ({ username, code, newPassword }) => {
    return confirmResetPassword({ username, confirmationCode: code, newPassword })
  },

  getMfaStatus: async () => {
    try {
      const { preferred, enabled } = await fetchMFAPreference()
      const isEnabled = preferred === 'TOTP' || (enabled && enabled.includes('TOTP'))
      return Promise.resolve(!!isEnabled)
    } catch (error) {
      console.log('MFA Preference Error', error)
      return Promise.resolve(false)
    }
  },

  setupMfa: async () => {
    const { sharedSecret } = await setUpTOTP()
    // Return the raw shared secret (base32) so callers can construct the otpauth URI as needed
    return Promise.resolve(sharedSecret)
  },

  verifyMfa: async ({ code, friendlyName = 'Admin Panel' }) => {
    await verifyTOTPSetup({ code })
    // No friendly name param in v6 verify 
    return Promise.resolve()
  },

  enableMfa: async () => {
    await updateMFAPreference({ totp: 'PREFERRED' })
    return Promise.resolve()
  },
}

export default cognitoProvider
